import { useEffect, useState, createContext, useContext } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  LayoutDashboard, Newspaper, HeartHandshake, Inbox,
  Mail, Users, ScrollText, Settings, LogOut, Menu, X,
} from "lucide-react";

const AdminContext = createContext(null);
export const useAdmin = () => useContext(AdminContext);

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/donations", label: "Donations", icon: HeartHandshake, permission: "donations.read_limited" },
  { to: "/admin/news", label: "News", icon: Newspaper, permission: "content.manage" },
  { to: "/admin/submissions", label: "Enquiries", icon: Inbox, permission: "submissions.read" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail, permission: "newsletter.manage" },
  { to: "/admin/users", label: "Users", icon: Users, permission: "users.invite" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, permission: "security.audit.read" },
  { to: "/admin/settings", label: "Settings", icon: Settings, permission: "settings.manage" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setUser] = useState(undefined);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin/login", { replace: true });
  };

  if (user === undefined) {
    return <div className="min-h-screen bg-ivory flex items-center justify-center text-charcoal/60">Loading…</div>;
  }

  const items = NAV.filter((n) => !n.permission || user.permissions.includes(n.permission));

  const navEl = (
    <nav className="flex flex-col gap-1" aria-label="Administration">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setMenuOpen(false)}
          data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-300 ${
              isActive ? "bg-white/10 text-white border-l-2 border-gold" : "text-white/70 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <item.icon size={17} strokeWidth={1.75} aria-hidden="true" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <AdminContext.Provider value={user}>
      <div className="min-h-screen bg-ivory lg:grid lg:grid-cols-[260px_1fr]">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex flex-col bg-forest text-white min-h-screen sticky top-0">
          <div className="px-5 py-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
              <span className="inline-block bg-ivory rounded-sm px-2 py-1.5 shrink-0">
                <img src="/logo-emblem.png" alt="Mount Moriah Relief logo" className="h-7 w-auto" />
              </span>
              <span className="font-serif text-lg font-semibold leading-tight">Mount Moriah Relief</span>
            </Link>
            <p className="mt-2 text-xs text-white/50">
              {user.role === "SUPER_ADMIN" ? "Super Administrator" : "Administrator"}
            </p>
          </div>
          <div className="flex-1 py-4">{navEl}</div>
          <div className="p-4 border-t border-white/10">
            <p className="text-xs text-white/50 truncate mb-3" data-testid="admin-user-email">{user.email}</p>
            <button onClick={logout} data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors duration-300">
              <LogOut size={15} aria-hidden="true" /> Sign out
            </button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden bg-forest text-white flex items-center justify-between px-5 h-16 sticky top-0 z-40">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block bg-ivory rounded-sm px-1.5 py-1">
              <img src="/logo-emblem.png" alt="Mount Moriah Relief logo" className="h-6 w-auto" />
            </span>
            <span className="font-serif font-semibold">MMR Admin</span>
          </Link>
          <button onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-label="Toggle menu" data-testid="admin-mobile-menu-toggle" className="p-2">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menuOpen && (
          <div className="lg:hidden bg-forest text-white px-2 pb-4">{navEl}
            <button onClick={logout} className="flex items-center gap-2 px-4 py-3 text-sm text-white/70" data-testid="admin-mobile-logout">
              <LogOut size={15} aria-hidden="true" /> Sign out
            </button>
          </div>
        )}

        <main className="min-w-0">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-12">
            <Outlet />
          </div>
        </main>
      </div>
    </AdminContext.Provider>
  );
}
